import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactGlobe from 'react-globe.gl';
import { PageContext } from './provider';
import { useWindowSize } from '../hooks/useWindowSize';
import earthDay from '../assets/earth-day.jpg';
import earthNight from '../assets/earth-night.jpg';
import countriesDataset from '../assets/countries.geojson';
import { globeConfigs, initConfigs } from '../context/constants';

const Globe = () => {
    const { state } = useContext(PageContext);

    const size = useWindowSize();

    const globeEl = useRef();

    const [ locations, setLocations ] = useState([]);

    const [ globeImageUrl, setGlobeImageUrl ] = useState(earthNight);

    const [ backgroundColor, setBackgroundColor ] = useState('#000011');

    const [ countries, setCountries ] = useState({ features: []});

    const orderPointColour = (domain, value) => {
        const color = globeConfigs[domain].salesChannels[value] ?? globeConfigs[domain].salesChannels.other;

        if (color instanceof Array) {
            return color;
        }

        const gradientColor = globeConfigs[domain]?.archStyle.gradientColor || color;

        if (color === gradientColor) {
            return [ color, gradientColor ];
        }

        const gradientCount = globeConfigs[domain]?.archStyle.gradientCount || 1;
        const colorArray = [];

        for (let i = 0; i <= gradientCount; i++) {
            colorArray.push(color);
            colorArray.push(gradientColor);
        }

        return colorArray;
    };

    const orderPointLabel = (domain, value) => {
        return domain + ' - ' + value;
    };

    useEffect(() => {
        // Get country data
        if (initConfigs.highlightCountries) {
            fetch(countriesDataset).then(res => res.json()).then(setCountries);
        }
    }, []);

    useEffect(() => {
        // Gen order data
        const arcsData = state.ordersData.map(function(order) {
            return {
                startLat: order.SourceLatLong[0],
                startLng: order.SourceLatLong[1],
                endLat: order.DestLatLong[0],
                endLng: order.DestLatLong[1],
                dashLength: globeConfigs[order.Domain]?.archStyle.dashLength || 0.5,
                dashGap: globeConfigs[order.Domain]?.archStyle.dashGap || 1,
                dashAnimateTime: globeConfigs[order.Domain]?.archStyle.dashAnimateTime || 5000,
                stroke: globeConfigs[order.Domain]?.archStyle.stroke || 0.1,
                label: orderPointLabel(order.Domain, order.SalesChannel || 'Other'),
                color: orderPointColour(order.Domain, order.SalesChannel?.toLowerCase() || 'other')
            };
        });

        setLocations(arcsData);
    }, [ state.ordersData ]);

    useEffect(() => {
        // Default Australia pointOfView and auto-rotate
        if (state.autoRotate) {
            globeEl.current.pointOfView(initConfigs.pointOfView);
        }
        globeEl.current.controls().autoRotate = state.autoRotate;
        globeEl.current.controls().autoRotateSpeed = initConfigs.autoRotateSpeed;
    }, [ state.autoRotate ]);

    useEffect(() => {
        // day or night globe
        if (state.isDayTime && !initConfigs.darkMode) {
            setGlobeImageUrl(earthDay);
            setBackgroundColor('#006baf');
        }
    }, [ state.isDayTime ]);

    return <ReactGlobe
        ref={globeEl}
        width={size.width}
        height={size.height}
        globeImageUrl={globeImageUrl}
        backgroundColor={backgroundColor}

        arcsData={locations}
        arcStartLat={'startLat'}
        arcStartLng={'startLng'}
        arcEndLat={'endLat'}
        arcEndLng={'endLng'}
        arcLabel={'label'}
        arcColor={'color'}
        arcAltitudeAutoScale={0.7}
        arcStroke={'stroke'}

        arcDashLength={'dashLength'}
        arcDashGap={'dashGap'}
        arcDashInitialGap={() => Math.random()}
        arcDashAnimateTime={'dashAnimateTime'}
        arcsTransitionDuration={0}

        polygonsData={countries.features}
        polygonAltitude={initConfigs.countriesAltitude}
        polygonCapColor={() => initConfigs.countriesCapColor}
        polygonSideColor={() => initConfigs.countriesSideColor}
        polygonStrokeColor={() => initConfigs.countriesStrokeColor}
        polygonCapCurvatureResolution={5}
    />;
};

export default Globe;
